import * as pmath from './pmath.js';

/** Free deployment hangs briefly before its stronger burst; held boost stays normal. */
export const FLIGHT_LAUNCH_WINDUP = .3;
export const FLIGHT_LAUNCH_TIME = .4;
export const FLIGHT_LAUNCH_TOTAL = FLIGHT_LAUNCH_WINDUP + FLIGHT_LAUNCH_TIME;
export const FLIGHT_LAUNCH_THRUST = 3.5;
export const FLIGHT_LAUNCH_BRAKE = .08;
export const FLIGHT_LAUNCH_RETAIN = .1;
/** Restore ordinary glide forces gently after deployment; this adds no thrust. */
export const FLIGHT_LAUNCH_RECOVERY = .4;

export class FlightLaunchRecovery {
  remaining=0;
  reset():void { this.remaining=0; }
  /** Average outgoing aerodynamic influence over the non-free part of a tick.
   * A real completed burst arms this once; cancellation must call reset. */
  update(dt:number,freeTime=0,completed=false):number {
    if(completed)this.remaining=FLIGHT_LAUNCH_RECOVERY;
    const elapsed=Math.max(0,(Number.isFinite(dt)?dt:0)-Math.max(0,Number.isFinite(freeTime)?freeTime:0));
    const from=this.remaining/FLIGHT_LAUNCH_RECOVERY;
    this.remaining=Math.max(0,this.remaining-elapsed);
    const to=this.remaining/FLIGHT_LAUNCH_RECOVERY;
    // Integral of smoothstep, so split/irregular frames have the same envelope.
    const integral=(t:number)=>t*t*t*(1-.5*t);
    return elapsed>1e-9
      ?FLIGHT_LAUNCH_RECOVERY*(integral(from)-integral(to))/elapsed
      :from*from*(3-2*from);
  }
}

/** X pitches the body over before a gravity-driven plunge; no thrust. The
 * pitch-over keeps most of the carried speed and swings its direction down
 * along the same curve as the look, so the entry is one continuous arc. */
export const DIVE_PREPARATION = {time:.3,brake:.3,retain:.6,pitch:1.35} as const;
export function divePreparationRetention(remaining:number):number {
  const t=Math.max(0,Math.min(1,(DIVE_PREPARATION.time-remaining)/DIVE_PREPARATION.brake));
  return DIVE_PREPARATION.retain+(1-DIVE_PREPARATION.retain)*(1-t*t*(3-2*t));
}
/** 0 → 1 across the preparation: how far travel has turned toward straight down. */
export function divePreparationTurn(remaining:number):number {
  const t=Math.max(0,Math.min(1,1-remaining/DIVE_PREPARATION.time));
  return t*t*t*(t*(t*6-15)+10);
}

/** The committed plunge. The pull is the original constant dive gravity up
 * to 80% of terminal speed (course geometry is planned on that curve), then
 * eases onto the terminal speed along an exponential with the same slope at
 * the knee, rather than slamming into a hard cap. Closed form in both
 * regions, so every frame rate lands on the same speed. */
export const DIVE_FALL = {terminal:66,gravityScale:1.55,knee:.8} as const;
/** Downward speed after `dt` of dive. Negative input (still rising) first
 * decelerates under the full pull; above terminal (a boosted entry) the
 * speed bleeds back down along the same exponential. */
export function diveFallSpeed(down:number,gravity:number,dt:number):number {
  if(!(dt>0))return down;
  const pull=gravity*DIVE_FALL.gravityScale,terminal=DIVE_FALL.terminal,knee=terminal*DIVE_FALL.knee;
  if(down<knee){
    const linear=(knee-down)/pull;
    if(dt<=linear)return down+pull*dt;
    dt-=linear;down=knee;
  }
  const tau=(terminal-knee)/pull;
  return terminal-(terminal-down)*pmath.exp(-dt/tau);
}

/** Touchdown flare: the downward speed allowed `clearance` metres above the
 * floor, so a dive always arrives at a readable landing speed. */
export const DIVE_BRAKE = {decel:62,touchdown:17} as const;
export function diveBrakeSpeed(clearance:number):number {
  return pmath.pow(DIVE_BRAKE.touchdown*DIVE_BRAKE.touchdown+2*DIVE_BRAKE.decel*Math.max(0,clearance),.5);
}

/** Retained velocity fraction during windup, measured on the total launch clock.
 * Apply the new/old fraction ratio so repeated steps do not compound braking. */
export function flightLaunchRetention(remaining: number): number {
  const elapsed = FLIGHT_LAUNCH_TOTAL - remaining;
  const t = Math.max(0, Math.min(1, elapsed / FLIGHT_LAUNCH_BRAKE));
  return FLIGHT_LAUNCH_RETAIN + (1 - FLIGHT_LAUNCH_RETAIN) * (1 - t * t * (3 - 2 * t));
}

/** Arcade flight tuning shared by the controller and procedural route solver. */
export const GLIDE = {
  stallSpeed: 8.5, recoverSpeed: 12.5, stallDelay: .18,
  thrust: 24, boostBurn: 22, boostSpeed: 65,
  drag: .003, poweredDrag: .0012, coastDrag: .65, turnDrag: .0045, brakeDrag: .85,
  gravityScale: 1.9, poweredGravityScale: 1.3, diveGravityScale: 1.55, lift: .22, poweredLift: .72,
  trimSink: 6.8, turnRate: 5.2, steerResponse: 7.5,
  // Ordinary low-speed glides still lose height. A committed dive or boost
  // earns a longer coast and a useful climb before that surplus dissipates.
  momentumStart: 28, momentumFull: 56, momentumCoastDrag: .12,
  pullUpRate: 4.8, diveTurnRate: 2.6, fullClimbSpeed: 34, maxClimbAngle: 1.05, overPullDrag: 1.75,
  descentOverspeedDrag: .3,
} as const;

/** Thruster-supported hover: inexpensive aiming time, never free altitude hold. */
export const HOVER = {sinkSpeed:1.8,fallResponse:8,burnFraction:.25,unpoweredSink:9.5} as const;
/** Shared with course feasibility checks. `support` is the funded fraction of this step. */
export function hoverVerticalVelocity(rise:number,gravity:number,support:number,dt:number):number {
  const falling=Math.max(-HOVER.unpoweredSink,rise-gravity*1.15*dt);
  const supported=-HOVER.sinkSpeed+(rise+HOVER.sinkSpeed)*pmath.exp(-HOVER.fallResponse*dt);
  return falling+(supported-falling)*support;
}

export interface FlightVelocity { horizontal: number; vertical: number }
export interface FlightStep {
  /** Positive camera pitch aims down. Path angle and vertical speed are up-positive. */
  pitch: number;
  gravity: number;
  power: number;
  thrustPower: number;
  /** Brief launch-to-glide force recovery, 0..1; never engine effort or fuel. */
  launchRecovery?: number;
  /** Actual horizontal turn rate, not camera movement. */
  yawRate: number;
  brake: boolean;
  dt: number;
}

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const smoothstep = (x: number, a: number, b: number) => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };

/** Metres of potential climb represented by existing excess kinetic energy.
 * This is a readout, never another store added on top of velocity. */
export function flightRisePotential(speed: number, gravity: number): number {
  return gravity > 0 ? Math.max(0, speed * speed - GLIDE.recoverSpeed ** 2) / (2 * gravity * GLIDE.gravityScale) : 0;
}

/** One bounded longitudinal step. The 3D caller owns yaw and radial-up framing.
 * Gravity and thrust alone add energy. Steering/lift rotate existing velocity,
 * and climbing pays for altitude through gravity on subsequent steps. */
export function stepFlightVelocity(state: FlightVelocity, input: FlightStep): void {
  const h = input.dt;
  if (!Number.isFinite(h) || h <= 0) return;
  const recovery = clamp(input.launchRecovery ?? 0, 0, 1);
  const effort = Math.max(clamp(input.power, 0, 1), recovery);
  const pull = input.gravity * (1 - recovery) * lerp(GLIDE.gravityScale, GLIDE.poweredGravityScale, effort);
  let vx = state.horizontal, vy = state.vertical - pull * h;
  let speed = Math.sqrt(vx * vx + vy * vy);
  // A gravity-free launch may begin completely still. Supply its direction
  // from aim, leaving speed at zero for the regular thrust calculation below.
  const restPath = clamp(-input.pitch, -Math.PI / 2 + .02, Math.PI / 2 - .02);
  const authority = smoothstep(speed, 4, 16);
  const dx = speed > 1e-8 ? vx / speed : pmath.cos(restPath);
  const dy = speed > 1e-8 ? vy / speed : pmath.sin(restPath);
  const support = lerp(GLIDE.lift, GLIDE.poweredLift, effort);
  vx += -dy * dx * pull * support * authority * h;
  vy += (1 - dy * dy) * pull * support * authority * h;
  const lifted = Math.sqrt(vx * vx + vy * vy);
  if (lifted > 1e-8) { vx *= speed / lifted; vy *= speed / lifted; }
  const path = speed > 1e-8 ? pmath.atan2(vy, vx) : restPath;
  const sink = (1 - effort) * pmath.atan2(GLIDE.trimSink * Math.min(1, input.gravity / 16), Math.max(speed, GLIDE.stallSpeed));
  let desired = clamp(-input.pitch - sink, -Math.PI / 2 + .02, Math.PI / 2 - .02);
  const climb = smoothstep(speed, GLIDE.recoverSpeed, GLIDE.fullClimbSpeed);
  const climbLimit = lerp(GLIDE.maxClimbAngle * climb, Math.PI / 2 - .02, effort);
  // Continuing to haul the nose up after spending the surplus sheds airspeed
  // into a stall. Merely leveling out resumes the ordinary descending glide.
  const overPull = Math.max(0, desired - climbLimit) * (1 - effort);
  desired = Math.min(desired, climbLimit);
  // Recover promptly from a dive while retaining a continuous, bounded arc.
  // Downward turns, low-speed and powered handling retain their existing rates.
  const surplus = smoothstep(speed, GLIDE.momentumStart, GLIDE.momentumFull);
  const highSpeedRate = desired > path ? GLIDE.pullUpRate : GLIDE.diveTurnRate;
  const pitchRate = lerp(GLIDE.turnRate, highSpeedRate, surplus * (1 - effort));
  const limit = pitchRate * (.4 + .6 * authority) * h;
  const nextPath = path + clamp((desired - path) * (1 - pmath.exp(-GLIDE.steerResponse * h)), -limit, limit);
  const pitchTurn = (nextPath - path) / h;
  const turnSquared = pitchTurn * pitchTurn + input.yawRate * input.yawRate * pmath.cos(nextPath) ** 2;
  const coast = lerp(GLIDE.coastDrag, GLIDE.momentumCoastDrag, surplus);
  const drag = lerp(GLIDE.drag, GLIDE.poweredDrag, effort) * speed + coast * (1 - effort);
  // A prolonged powered diagonal descent must not outrun the dedicated dive
  // or network movement limits. Air resistance ramps in near terminal speed;
  // releasing into a pull-up keeps the full earned velocity without a clamp.
  const overspeed = Math.max(0, speed - GLIDE.boostSpeed) * GLIDE.descentOverspeedDrag * smoothstep(input.pitch, .2, .7);
  speed *= pmath.exp(-(drag + overspeed + GLIDE.turnDrag * turnSquared + GLIDE.overPullDrag * overPull + (input.brake ? GLIDE.brakeDrag : 0)) * h);
  speed += GLIDE.thrust * Math.max(0, input.thrustPower) * Math.max(0, 1 - speed / GLIDE.boostSpeed) * h;
  state.horizontal = speed * pmath.cos(nextPath);
  state.vertical = speed * pmath.sin(nextPath);
}
